import { collectOnClickAnalyticsData, runAnalyticsOnClick } from '@xp-utilities/web';
import { Button, ButtonGroup } from 'anf-core-react';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useLocalStorage } from 'react-use';
import BreakpointContext from '../../context/breakpoint';
import useTranslatedText from '../../hooks/useTranslatedText';
import { PERSISTENT_ANALYTICS_DATA_KEY } from '../../tools/constants';
import SkeletonProductGrid from '../ProductGrid/SkeletonGrid';
import listStyle from '../SearchDefaultView/SearchDefaultView.module.scss';
import SimpleProductCard from '../SimpleProductCard/SimpleProductCard';
import Slider from '../Slider';
import style from './AutoSuggest.module.scss';
import AutoSuggestSubList from './AutoSuggestSubList';
import useBestSuggestion from './hooks/useBestSuggestion';

export default function AutoSuggestResult({
  isFormFocused = false,
  isProcessing = false,
  listItemSubmitHandler,
  listPosition,
  productTypesData = [],
  productData = [],
  searchTerm = undefined,
  searchLabel = 'Search',
}) {
  const processing = useTranslatedText('processing', { fallback: 'Processing' });
  const products = useTranslatedText('autoSuggestProducts', { fallback: 'Products' });
  const suggestions = useTranslatedText('autoSuggestSuggestions', { fallback: 'Suggestions' });
  const seeAllResults = useTranslatedText('autoSuggestAllResults', { fallback: 'See all results for' });
  const { large: isDesktop } = useContext(BreakpointContext);
  const [, setIsQuickViewOpen] = useState(false);
  const [, setAlgonomyPersistentData] = useLocalStorage(PERSISTENT_ANALYTICS_DATA_KEY, {});

  const prepareSimpleCardData = (item) => ({
    collection: (item.collection || item.collectionId).toString(),
    defaultSwatchSequence: item.defaultSwatchSequence,
    imageId: item.imageId,
    name: item.name,
    primaryFaceOutImage: `${item.imageId}_${item.primaryFaceOut || 'prod1'}`,
    productId: item.productId,
    imageSet: item.imageSet,
    imageSetProd: item?.imageSetProd ?? [],
    imageSetModel: item?.imageSetModel ?? [],
  });

  const {
    searchTypePlacements,
    showRecommendations,
    algonomyProductsLoading,
  } = useBestSuggestion([...productData, ...productTypesData], searchTerm);

  const productSuggestions = () => {
    if (algonomyProductsLoading) {
      if (isDesktop) {
        return (
          <div className={style.suggestionsSkeletonWrapper}>
            <SkeletonProductGrid cardAmount={3} columns={3} includeSwatches={false} />
          </div>
        );
      }

      return (
        <SkeletonProductGrid cardAmount={6} columns={2} includeSwatches={false} />
      );
    }

    if (showRecommendations && searchTypePlacements && searchTypePlacements.length > 0) {
      const algonomyProducts = searchTypePlacements[0].products;
      const { id, name, treatments } = searchTypePlacements[0];
      const algonomyAnalyticsObj = {
        placementObj: {
          id,
          name,
          treatments,
        },
        productIndex: 0,
      };

      const handleProductCardClick = (departmentName, productName) => {
        const fixedDepartment = departmentName
          ? departmentName.charAt(0).toUpperCase() + departmentName.slice(1) : null;

        const analyticsObj = collectOnClickAnalyticsData(
          searchTypePlacements[0],
          algonomyAnalyticsObj.productIndex,
        );

        // need to set the analytics on databridge when clicked
        runAnalyticsOnClick(analyticsObj);

        setAlgonomyPersistentData({
          pagefm: `recommendations-${analyticsObj?.strategyName || ''}`,
          precPos: analyticsObj?.position,
          precPla: analyticsObj?.placement,
        });
        listItemSubmitHandler({ departmentName: fixedDepartment, value: productName }, 'products');
      };

      const algonomySuggestionCardData = algonomyProducts?.map((item, index) => {
        const algonomyProduct = JSON.parse(item.response);
        algonomyAnalyticsObj.productIndex = index;
        return ({
          key: algonomyProduct.imageId,
          productCard: (
            <SimpleProductCard
              key={algonomyProduct.imageId}
              algonomyOnClickAnalytics={algonomyAnalyticsObj}
              brand={algonomyProduct.brand}
              handleProductCardClick={() => handleProductCardClick(
                algonomyProduct.departmentName, algonomyProduct.name,
              )}
              index={index}
              item={prepareSimpleCardData(algonomyProduct)}
              listPosition={listPosition - (algonomyProducts?.length ?? 0)}
              setIsQuickViewOpen={() => setIsQuickViewOpen(true)}
            />
          ),
        });
      });

      const returnValue = isDesktop ? (
        <div className={style.suggestionsSliderWrapper}>
          <Slider
            arrowWidth={40}
            maxSlidesVisible={7}
            slideContent={algonomySuggestionCardData}
            slideWidth={180}
          />
        </div>
      ) : (
        <div className={style.grid}>
          {algonomySuggestionCardData.map((item) => (
            item.productCard
          ))}
        </div>
      );

      return returnValue;
    }

    return (
      (
        <AutoSuggestSubList
          data={productData}
          listItemSubmitHandler={listItemSubmitHandler}
          listPosition={listPosition - (productTypesData?.length ?? 0)}
          searchTerm={searchTerm}
          suggestionType="products"
        />
      )
    );
  };

  return (
    <div
      aria-label={searchLabel}
      className={`auto-suggest-list-menu-wrapper fixed-list-container ${listStyle.listWrapper}`}
      data-dynamic-suggestions="true"
      data-expanded={!!isFormFocused}
      data-focused={!!isFormFocused}
      id="auto-suggest-desktop-list"
      role="listbox"
      tabIndex="-1"
    >
      <div className={`${listStyle.listContainer} ${listStyle.suggestionsListContainer}`} data-testid="auto-suggest-list-menu-wrapper">
        {productTypesData?.length > 0 ? (
          <>
            <div data-testid="auto-suggest-list-suggestions">
              <h2 className="h3">{suggestions.value}</h2>
              <AutoSuggestSubList
                data={productTypesData}
                listItemSubmitHandler={listItemSubmitHandler}
                listPosition={listPosition}
                searchTerm={searchTerm}
                suggestionType="suggestions"
              />
            </div>
          </>
        ) : null}
        {productData?.length > 0 ? (
          <>
            <div className={style.suggestionsWrapper} data-testid="auto-suggest-list-products">
              <h2 className="h3">{products.value}</h2>
              {productSuggestions()}
            </div>
          </>
        ) : null}
        <ButtonGroup variant="vertical-spaced">
          <Button
            isProcessing={isProcessing}
            type="submit"
            value={searchTerm}
            variant="secondary"
          >
            {isProcessing ? `${processing.value}...` : `${seeAllResults.value} "${searchTerm}"`}
          </Button>
        </ButtonGroup>
        {isProcessing && (productTypesData?.length > 0 || productData?.length > 0) ? (
          <div className={style.spinWrapper}>
            <div className={style.dotSpin} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

AutoSuggestResult.propTypes = {
  productTypesData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  isFormFocused: PropTypes.bool,
  isProcessing: PropTypes.bool,
  listItemSubmitHandler: PropTypes.func.isRequired,
  listPosition: PropTypes.number.isRequired,
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
    }),
  ),
  searchLabel: PropTypes.string,
  searchTerm: PropTypes.string,
};
